import { useEffect, useMemo, useRef, useState } from "react";
import { Navigation, Pagination } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";
import type { Swiper as SwiperType } from "swiper";
import {
  CampaignType,
  Offer,
  OffersIdSlots,
  Template,
  TemplateOptionSettings,
} from "../../../../types/global.types";
import OfferItem from "../offer-item/OfferItem.component";

interface Props {
  sessionId: string;
  displayOnly: boolean;
  settings: TemplateOptionSettings;
  offers: Offer[];
  campaign: CampaignType;
  settingsTemplate: Template;
  campaignId: string;
  placementId: string;
  placementName: string;
  isSeen: boolean;
  preEmail: string | undefined;
  setPreEmail?: (email: string) => void;
  openSingle: (
    o: Offer,
    isSave: boolean,
    isAutoEmail: boolean,
    slot: number,
    isShare?: boolean
  ) => void;
  onAdd: (offer: Offer, value: boolean, slot: number) => void;
  layoutId: string;
  canAutoSendEmailFromLabel: boolean;
  canAutoSendEmailFromMainCTA: boolean;
  onAutoSendEmailFromLabel: (offer: Offer, slot: number) => Promise<void>;
  autoSendingOfferId: string;
}

const SwiperComponent = ({
  sessionId,
  isSeen,
  settings,
  preEmail,
  setPreEmail,
  campaign,
  offers,
  displayOnly,
  campaignId,
  placementId,
  placementName,
  settingsTemplate,
  openSingle,
  onAdd,
  layoutId,
  canAutoSendEmailFromLabel,
  canAutoSendEmailFromMainCTA,
  onAutoSendEmailFromLabel,
  autoSendingOfferId,
}: Props) => {
  const refNavPrev = useRef<HTMLDivElement | null>(null);
  const refNavNext = useRef<HTMLDivElement | null>(null);
  const refPagination = useRef<HTMLDivElement | null>(null);
  const swiperRef = useRef<SwiperType | null>(null);
  const [eventOfferIds, setEventOfferIds] = useState<OffersIdSlots[]>([]);

  const numberOfOffers = offers.length;
  const offersNumberSetting = settings?.mainLayout.offersNumber;
  const isAutoSlides = offersNumberSetting === "auto";
  const fixedColumns = !isAutoSlides ? Number(offersNumberSetting) || 3 : 3;
  const slidesPerView = isAutoSlides ? "auto" : fixedColumns;

  const showNavigation = useMemo(() => {
    if (isAutoSlides) {
      return offers.length > 1;
    }
    return offers.length > fixedColumns;
  }, [offers.length, isAutoSlides, fixedColumns]);

  useEffect(() => {
    if (isSeen) {
      setEventOfferIds(
        offers.map((offer: Offer, index: number) => ({
          id: offer.id,
          slot: offer.slot || index + 1,
        }))
      );
    }
  }, [isSeen, offers]);

  useEffect(() => {
    if (!swiperRef.current) return;

    const frameId = requestAnimationFrame(() => {
      if (!swiperRef.current) return;

      const shouldShowNavigation = isAutoSlides
        ? offers.length > 1
        : offers.length > fixedColumns;

      if (shouldShowNavigation && refNavPrev.current && refNavNext.current) {
        swiperRef.current.params.navigation = {
          ...(swiperRef.current.params.navigation as object),
          prevEl: refNavPrev.current,
          nextEl: refNavNext.current,
        };

        swiperRef.current.navigation.destroy();
        swiperRef.current.navigation.init();
        swiperRef.current.navigation.update();
      }

      if (shouldShowNavigation && refPagination.current) {
        swiperRef.current.params.pagination = {
          ...(swiperRef.current.params.pagination as object),
          el: refPagination.current,
        };
        swiperRef.current.pagination.destroy();
        swiperRef.current.pagination.init();
        swiperRef.current.pagination.render();
        swiperRef.current.pagination.update();
      }

      swiperRef.current.update();
    });

    return () => cancelAnimationFrame(frameId);
  }, [
    numberOfOffers,
    offersNumberSetting,
    isAutoSlides,
    fixedColumns,
    settings,
    campaign,
    offers,
    showNavigation,
  ]);

  const hasVoucher = offers.some(
    (offer) => offer.voucher && offer.voucher.voucherType
  );

  return (
    <>
      {offers.length > 0 && (
        <>
          <Swiper
            key={`swiper-${numberOfOffers}-${offersNumberSetting}-${showNavigation}-${settings.mainLayout.align}`}
            className={`swiper-container swiper-column-${offersNumberSetting}`}
            modules={[Navigation, Pagination]}
            watchSlidesProgress={true}
            pagination={
              showNavigation
                ? {
                    clickable: true,
                    el: refPagination.current || ".swiper-pagination-category",
                  }
                : false
            }
            navigation={showNavigation}
            onSwiper={(swiper: SwiperType) => {
              swiperRef.current = swiper;
            }}
            slidesPerView={slidesPerView}
            slidesPerGroupAuto={isAutoSlides}
            loop={showNavigation && !isAutoSlides}
          >
            {offers.map((o: Offer, i: number) => (
              <SwiperSlide
                key={o.id}
                style={{ color: settings?.dealLayout.textColor }}
              >
                <OfferItem
                  sessionId={sessionId}
                  offer={o}
                  slot={o.slot || i + 1}
                  eventOfferIds={eventOfferIds}
                  settings={settings}
                  isAdded={o.isAdded || false}
                  settingsTemplate={settingsTemplate}
                  onAdd={onAdd}
                  displayOnly={displayOnly}
                  campaign={campaign}
                  retailerId={campaign?.company_Id}
                  campaignId={campaignId}
                  placementId={placementId}
                  placementName={placementName}
                  openSingle={openSingle}
                  setPreEmail={setPreEmail}
                  preEmail={preEmail}
                  hasVoucher={hasVoucher}
                  otherSettings={settings.otherSettings}
                  layoutId={layoutId}
                  layoutOptions={settings.layoutOptions}
                  canAutoSendEmailFromLabel={canAutoSendEmailFromLabel}
                  canAutoSendEmailFromMainCTA={canAutoSendEmailFromMainCTA}
                  onAutoSendEmailFromLabel={onAutoSendEmailFromLabel}
                  autoSendingOfferId={autoSendingOfferId === o.id}
                />
              </SwiperSlide>
            ))}
          </Swiper>

          {showNavigation && (
            <div className="swiper-navigation-category">
              <div className="swiper-button-prev" ref={refNavPrev}>
                <svg
                  width="34"
                  height="34"
                  viewBox="0 0 34 34"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <circle
                    cx="17"
                    cy="17"
                    r="17"
                    transform="rotate(-180 17 17)"
                    fill={settings.dealLayout.backgroundColor}
                  />
                  <rect
                    x="18.6094"
                    y="23"
                    width="8.6192"
                    height="1.72384"
                    rx="0.86192"
                    transform="rotate(-135 18.6094 23)"
                    fill={settings.dealLayout.textColor}
                  />
                  <rect
                    x="20.3135"
                    y="12.2188"
                    width="8.6192"
                    height="1.72384"
                    rx="0.86192"
                    transform="rotate(135 20.3135 12.2188)"
                    fill={settings.dealLayout.textColor}
                  />
                </svg>
              </div>

              <div
                className="swiper-pagination-category"
                ref={refPagination}
              ></div>

              <div className="swiper-button-next" ref={refNavNext}>
                <svg
                  width="34"
                  height="34"
                  viewBox="0 0 34 34"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <circle
                    cx="17"
                    cy="17"
                    r="17"
                    fill={settings.dealLayout.backgroundColor}
                  />
                  <rect
                    x="15.3906"
                    y="11"
                    width="8.6192"
                    height="1.72384"
                    rx="0.86192"
                    transform="rotate(45 15.3906 11)"
                    fill={settings.dealLayout.textColor}
                  />
                  <rect
                    x="14.1719"
                    y="22.2656"
                    width="8.6192"
                    height="1.72384"
                    rx="0.86192"
                    transform="rotate(-45 14.1719 22.2656)"
                    fill={settings.dealLayout.textColor}
                  />
                </svg>
              </div>
            </div>
          )}
        </>
      )}
    </>
  );
};

export default SwiperComponent;
